// ──────────────────────────────────────────────
// SessionRecoveryService — crash recovery for orphaned recording sessions.
//
// On app load, scans for sessions with un-uploaded chunks in local storage
// and auto-recovers them by re-uploading and completing.
//
// See ADR-007 for design rationale.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import { RECORDING_LOCK_NAME } from '../recording/types';
import type { StorageService } from '../storage';
import { BaseService } from '../upload/BaseService';
import type { UploadService } from '../upload/UploadService';

import type { SessionCompletionService } from './SessionCompletionService';
import type { SessionManifest } from './SessionManifest';
import type {
  RecoveryEventMap,
  RecoveryResult,
  RecoveryState,
  RecoveryStatus,
} from './types';

// Sessions older than 7 days may have had storage evicted by Safari ITP
const STALE_SESSION_DAYS = 7;

function initialState(): RecoveryState {
  return {
    status: 'idle',
    activeRecovery: null,
    recoveredSessions: [],
    failedRecoveries: [],
  };
}

export interface SessionRecoveryConfig {
  storageService: StorageService;
  uploadService: UploadService;
  completionService: SessionCompletionService;
  sessionManifest: SessionManifest;
}

/**
 * On app load, scans for orphaned recording sessions and auto-recovers them.
 *
 * Recovery flow per session:
 * 1. List remaining chunks in StorageService
 * 2. Filter out already-uploaded chunks (from manifest)
 * 3. Enqueue remaining chunks to UploadService
 * 4. Call completionService.completeSession() for drain + recording-end
 * 5. Clean up local storage on success
 * 6. Emit notification event
 */
export class SessionRecoveryService extends BaseService<RecoveryState, RecoveryEventMap> {
  private readonly storageService: StorageService;
  private readonly uploadService: UploadService;
  private readonly completionService: SessionCompletionService;
  private readonly sessionManifest: SessionManifest;
  private disposed = false;

  constructor(config: SessionRecoveryConfig) {
    super(initialState());

    this.storageService = config.storageService;
    this.uploadService = config.uploadService;
    this.completionService = config.completionService;
    this.sessionManifest = config.sessionManifest;
  }

  // ── Public API ──

  /**
   * Scan for orphaned sessions and auto-recover each one.
   * Call once on app load.
   */
  async scan(): Promise<void> {
    if (this.disposed) return;

    // If another tab is actively recording, skip scan entirely.
    // The recording holds a Web Lock that auto-releases on tab crash,
    // so orphaned sessions will be found on the next load after recording stops.
    if (await this.isRecordingLockHeld()) {
      this.setRecoveryStatus('complete');
      this.emit('scanComplete', { found: 0, recovered: 0, failed: 0, cleaned: 0 });
      return;
    }

    this.setRecoveryStatus('scanning');

    try {
      // 1. Collect orphaned sessions from both manifest and storage
      const orphanedSessionIds = await this.findOrphanedSessions();

      if (orphanedSessionIds.length === 0) {
        this.setRecoveryStatus('complete');
        this.emit('scanComplete', { found: 0, recovered: 0, failed: 0, cleaned: 0 });
        return;
      }

      SentryService.addBreadcrumb({
        category: 'session.recovery',
        message: `Found ${orphanedSessionIds.length} orphaned session(s)`,
        level: 'info',
        data: { sessionIds: orphanedSessionIds },
      });

      this.setRecoveryStatus('recovering');

      // 2. Process each session sequentially (don't overwhelm upload pipeline)
      let recovered = 0;
      let failed = 0;
      let cleaned = 0;

      for (const sessionId of orphanedSessionIds) {
        if (this.disposed) break;

        try {
          const result = await this.recoverSession(sessionId);
          if (result) {
            this.setState({
              ...this.state,
              recoveredSessions: [...this.state.recoveredSessions, result],
            });
            this.emit('sessionRecovered', { result });
            recovered++;
          } else {
            cleaned++;
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          this.setState({
            ...this.state,
            failedRecoveries: [...this.state.failedRecoveries, { sessionId, error: errorMsg }],
          });
          this.emit('sessionFailed', { sessionId, error: errorMsg });
          failed++;

          SentryService.captureException(
            error instanceof Error ? error : new Error(`Recovery failed for ${sessionId}: ${errorMsg}`),
          );
        }
      }

      this.setRecoveryStatus('complete');
      this.setState({ ...this.state, activeRecovery: null });
      this.emit('scanComplete', { found: orphanedSessionIds.length, recovered, failed, cleaned });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      SentryService.captureException(
        error instanceof Error ? error : new Error(`Recovery scan failed: ${message}`),
      );
      this.setRecoveryStatus('complete');
    }
  }

  /**
   * Recover a single session: re-upload remaining chunks and complete.
   * Returns null when no chunks needed re-uploading (stale manifest cleanup).
   */
  async recoverSession(sessionId: string): Promise<RecoveryResult | null> {
    const manifest = await this.sessionManifest.get(sessionId);

    // List chunks still in local storage
    const storedChunks = await this.storageService.listChunks(sessionId);

    if (storedChunks.length === 0) {
      // No chunks to re-upload — this is stale manifest cleanup, not recovery.
      const totalChunks = manifest?.totalChunks ?? 0;

      if (totalChunks > 0 && manifest && !manifest.recordingEndConfirmed) {
        // Chunks were already uploaded but session wasn't finalized — call recording-end
        await this.completionService.completeSession(sessionId, totalChunks);
      }

      // Clean up stale manifest + any leftover storage
      await this.storageService.deleteSession(sessionId);
      await this.sessionManifest.delete(sessionId);

      const reason = totalChunks === 0 ? 'empty_session' : 'already_uploaded';
      this.emit('staleManifestCleaned', { sessionId, reason, totalChunks });

      SentryService.addBreadcrumb({
        category: 'session.recovery',
        message: `Stale manifest cleaned for ${sessionId} (${reason}, totalChunks=${totalChunks})`,
        level: 'info',
      });

      return null;
    }

    // Filter out already-uploaded chunks
    const uploadedSet = new Set(manifest?.uploadedChunks ?? []);
    const chunksToUpload = storedChunks.filter(idx => !uploadedSet.has(idx));
    const totalChunks = manifest?.totalChunks ?? storedChunks.length;

    // Check for stale session
    if (manifest?.createdAt) {
      const ageMs = Date.now() - new Date(manifest.createdAt).getTime();
      if (ageMs > STALE_SESSION_DAYS * 24 * 60 * 60 * 1000) {
        SentryService.addBreadcrumb({
          category: 'session.recovery',
          message: `Session ${sessionId} is ${Math.floor(ageMs / 86400000)} days old — data may be partially evicted`,
          level: 'warning',
        });
      }
    }

    // Update active recovery progress
    this.setState({
      ...this.state,
      activeRecovery: {
        sessionId,
        totalChunks,
        uploadedChunks: 0,
        failedChunks: 0,
        progress: 0,
      },
    });

    SentryService.addBreadcrumb({
      category: 'session.recovery',
      message: `Recovering session ${sessionId}: ${chunksToUpload.length} chunks to upload (${storedChunks.length} in storage, ${uploadedSet.size} already uploaded)`,
      level: 'info',
    });

    // Enqueue remaining chunks to UploadService
    const contentType = manifest?.mimeType ?? 'audio/webm;codecs=opus';
    for (const chunkIndex of chunksToUpload) {
      const chunkData = await this.storageService.readChunk(sessionId, chunkIndex);
      if (!chunkData) continue;

      this.uploadService.enqueue({
        sessionId,
        chunkIndex,
        chunkId: `${sessionId}-recovery-${chunkIndex}`,
        contentType,
        byteSize: chunkData.byteLength,
      });
    }

    // Complete the session (drain + recording-end)
    const completionResult = await this.completionService.completeSession(sessionId, totalChunks);

    // Clean up local storage on success
    await this.storageService.deleteSession(sessionId);
    await this.sessionManifest.delete(sessionId);

    const result: RecoveryResult = {
      sessionId,
      encounterId: manifest?.encounterId,
      totalChunks,
      uploadedChunks: completionResult.uploadedChunks,
      failedChunks: completionResult.failedChunks,
      recordingEndCalled: true,
    };

    SentryService.addBreadcrumb({
      category: 'session.recovery',
      message: `Session ${sessionId} recovered (uploaded=${result.uploadedChunks}, failed=${result.failedChunks})`,
      level: 'info',
    });

    return result;
  }

  /** Discard an orphaned session without recovering it. */
  async discardSession(sessionId: string): Promise<void> {
    await this.storageService.deleteSession(sessionId);
    await this.sessionManifest.delete(sessionId);

    SentryService.addBreadcrumb({
      category: 'session.recovery',
      message: `Discarded session ${sessionId}`,
      level: 'info',
    });
  }

  /**
   * Clear recovery results after they've been acknowledged by the UI.
   * Prevents stale results from re-triggering the banner on encounter navigation.
   */
  acknowledge(): void {
    if (this.state.status !== 'complete') return;
    this.setState(initialState());
  }

  override dispose(): void {
    this.disposed = true;
    this.setState(initialState());
    super.dispose();
  }

  // ── Private ──

  /**
   * Find orphaned sessions by cross-referencing manifests and storage.
   * Returns session IDs that have local data but weren't fully completed.
   */
  private async findOrphanedSessions(): Promise<string[]> {
    const orphaned = new Set<string>();

    // Check manifests for incomplete sessions
    const manifests = await this.sessionManifest.listAll();
    for (const manifest of manifests) {
      if (!manifest.recordingEndConfirmed) {
        orphaned.add(manifest.sessionId);
      }
    }

    // Check storage for sessions — either without manifests (orphaned)
    // or with confirmed manifests that still have leftover chunks (need cleanup)
    const storedSessionIds = await this.storageService.listSessions();
    const manifestIds = new Set(manifests.map(m => m.sessionId));
    const confirmedManifests = new Set(
      manifests.filter(m => m.recordingEndConfirmed).map(m => m.sessionId),
    );

    for (const sessionId of storedSessionIds) {
      // Skip the dead letter storage key
      if (sessionId.startsWith('__')) continue;

      if (!manifestIds.has(sessionId)) {
        // Session has chunks but no manifest — orphaned
        orphaned.add(sessionId);
      } else if (confirmedManifests.has(sessionId)) {
        // Confirmed session with leftover storage — needs cleanup
        orphaned.add(sessionId);
      }
    }

    // Separate into sessions needing recovery vs just cleanup
    const toRecover: string[] = [];
    const toCleanup: string[] = [];

    for (const sessionId of orphaned) {
      const manifest = manifests.find(m => m.sessionId === sessionId);
      if (!manifest) {
        // No manifest = likely completed recording with leftover storage artifacts.
        // The manifest was deleted when the recording finished successfully;
        // these chunks are residue (e.g. from OPFS fallback migration).
        toCleanup.push(sessionId);
      } else if (manifest.recordingEndConfirmed) {
        toCleanup.push(sessionId);
      } else {
        toRecover.push(sessionId);
      }
    }

    // Silently clean up confirmed sessions
    for (const sessionId of toCleanup) {
      await this.storageService.deleteSession(sessionId);
      await this.sessionManifest.delete(sessionId);
    }

    return toRecover;
  }

  /**
   * Check if the recording Web Lock is currently held by any tab.
   * RecordingService acquires this lock for the duration of recording
   * and the browser auto-releases it on tab crash.
   */
  private async isRecordingLockHeld(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return false; // API not available — proceed with scan
    }

    try {
      const { held } = await navigator.locks.query();
      return held?.some(lock => lock.name === RECORDING_LOCK_NAME) ?? false;
    } catch {
      return false; // Query failed — proceed with scan
    }
  }

  private setRecoveryStatus(status: RecoveryStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.setState({ ...this.state, status });
    this.emit('statusChanged', { status, prev });
  }
}
